import React, { useState, useEffect } from 'react';
import { Proposal } from '../types';
import { transformPDFUrl } from '../utils/pdfUtils';
import ProposalActions from '../components/ProposalActions';
import SuccessMessage from '../components/SuccessMessage';
import NotFound from '../components/NotFound';

type ViewState = 'proposal' | 'success-approve' | 'success-decline' | 'not-found';

const TestPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('proposal');

  useEffect(() => {
    // Set page title for test page
    document.title = 'Proposal #test-proposal-123 - Cinch (Test Mode)';
  }, []);

  // Mock proposal data for testing
  const mockProposal: Proposal = {
    id: 123,
    customer: "John Smith",
    pdf_url: "app/public/proposals/dummy.pdf",
    created_at: "2025-10-01T10:00:00Z",
    updated_at: "2025-10-01T10:00:00Z",
    user_id: 1,
    data: {
      customer: "John Smith",
      notes: undefined,
      items: [
        ["Website Redesign", "Complete redesign of company website", "0", "1"]
      ],
      tax: "0",
      taxPercentage: "0",
      disclaimer: "<p>Terms and conditions apply</p>",
      customerMessage: "<p>Thank you for your business!</p>",
      templateTitle: "Website Development",
      templateDescription: "<p>Professional web development services</p>",
      price: 5000,
      times: 1,
      residentialPrice: {
        subtotal: 5000,
        tax: "0",
        total: 5000
      }
    },
    type: "commercial",
    selected_days: undefined,
    html_path: "app/proposals/dummy.html",
    office_id: undefined,
    is_residential: 0,
    files: [],
    status: "pending"
  };

  const handleSuccess = (action: "approve" | "decline", reason?: string) => {
    setCurrentView(
      action === "approve" ? "success-approve" : "success-decline"
    );
  };

  const renderProposalView = () => (
    <div className="min-h-screen bg-gradient-to-br from-cinch-light to-white">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 py-4 sm:py-6">
            <div className="flex items-center min-w-0">
              <img
                src="https://cleancinch.com/wp-content/uploads/2025/05/aa836ec1-54ef-4339-9b94-8b6eec324faa.png"
                alt="Cinch Logo"
                className="h-8 sm:h-10 w-auto mr-3 sm:mr-4 flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-cinch-primary truncate">
                  Proposal #{mockProposal.id} (Test Mode)
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                  Test environment - no real data will be modified
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <button
                onClick={() => setCurrentView('not-found')}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Test 404
              </button>
              <button
                onClick={() => setCurrentView('proposal')}
                className="text-sm text-cinch-primary hover:text-cinch-secondary"
              >
                Back to Proposal
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-sm font-medium text-yellow-800">Test Environment</h3>
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            This is a test page with mock data. No real proposals will be affected by actions taken here.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Same mobile-first ordering as DocumentView.tsx -actions/details
              before the PDF preview on small screens. */}
          <div className="order-2 lg:order-1 lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-cinch-primary mb-3 sm:mb-4">
                Proposal Document
              </h3>
              <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                  <iframe
                    src={transformPDFUrl(mockProposal.pdf_url)}
                    className="w-full aspect-[1/1.414] min-h-[380px] sm:min-h-[560px] lg:min-h-[800px] border border-gray-300 rounded-lg"
                    title="Proposal PDF (Test)"
                    scrolling="no"
                    style={{ overflow: 'hidden' }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-1 space-y-4 sm:space-y-6">
            <ProposalActions
              secretCode="test-123"
              onSuccess={handleSuccess}
              disabled={false}
            />

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-cinch-primary mb-4">
                Proposal Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Type
                  </label>
                  <p className="text-gray-900 font-medium">
                    {mockProposal.data.templateTitle}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <div className="text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: mockProposal.data.templateDescription }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <p className="text-gray-900">{mockProposal.customer}</p>
                  <p className="text-gray-600 text-sm capitalize">
                    {mockProposal.type}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-gray-600 text-sm">
                    {new Date(mockProposal.created_at).toLocaleDateString()}
                  </p>
                </div>
                {mockProposal.data.residentialPrice && mockProposal.data.residentialPrice.total > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Amount
                    </label>
                    <p className="text-gray-900 font-medium">
                      ${mockProposal.data.residentialPrice.total}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (currentView === "success-approve") {
    return (
      <SuccessMessage action="approve" proposalTitle={mockProposal.data.templateTitle} />
    );
  }

  if (currentView === "success-decline") {
    return (
      <SuccessMessage
        action="decline"
        reason="The timeline doesn't work for our current project schedule."
        proposalTitle={mockProposal.data.templateTitle}
      />
    );
  }

  if (currentView === "not-found") {
    return <NotFound />;
  }

  return renderProposalView();
};

export default TestPage;
